import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

export function formatApiErrorDetail(detail) {
    if (detail == null) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail === "object") {
        if (typeof detail.msg === "string") return detail.msg;
        if (typeof detail.message === "string") return detail.message;
        if (typeof detail.code === "string") return detail.code;
    }
    return String(detail);
}

// Returns the backend-provided error code (from a structured HTTPException
// detail like `{code, message}`) so pages can translate consistently
// instead of relying on brittle substring matches on the message.
export function extractApiErrorCode(detail) {
    if (detail && typeof detail === "object" && !Array.isArray(detail) && typeof detail.code === "string") {
        return detail.code;
    }
    return null;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
            setUser(response.data);
        } catch (error) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const response = await axios.post(
                `${API}/api/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setUser(response.data);
            return { success: true };
        } catch (error) {
            const detail = error.response?.data?.detail;
            return {
                success: false,
                errorCode: extractApiErrorCode(detail),
                error: formatApiErrorDetail(detail) || error.message
            };
        }
    };

    const register = async (name, email, password, organizationName) => {
        try {
            const response = await axios.post(
                `${API}/api/auth/register`,
                { name, email, password, organization_name: organizationName },
                { withCredentials: true }
            );
            // v3.17: NO auto-login. Register returns verification_required
            // and the backend does NOT set any cookie. The frontend must
            // treat the user as unauthenticated until they verify their
            // email AND log in explicitly.
            return {
                success: true,
                requiresVerification: true,
                email: response.data?.email || email,
                emailSent: response.data?.email_sent !== false,
                message: response.data?.message,
            };
        } catch (error) {
            const detail = error.response?.data?.detail;
            return {
                success: false,
                errorCode: extractApiErrorCode(detail),
                error: formatApiErrorDetail(detail) || error.message
            };
        }
    };

    const resendVerification = async (email) => {
        try {
            const res = await axios.post(
                `${API}/api/auth/resend-verification`,
                { email },
                { withCredentials: true }
            );
            return { success: true, message: res.data?.message };
        } catch (error) {
            const detail = error.response?.data?.detail;
            return {
                success: false,
                errorCode: extractApiErrorCode(detail),
                error: formatApiErrorDetail(detail) || error.message
            };
        }
    };

    const verifyEmail = async (token) => {
        try {
            const res = await axios.post(
                `${API}/api/auth/verify-email`,
                { token },
                { withCredentials: true }
            );
            // Never sets a session — the frontend must send the user to /login.
            return { success: true, status: res.data?.status || "verified" };
        } catch (error) {
            const detail = error.response?.data?.detail;
            const code = (detail && typeof detail === "object" && detail.code) || "invalid";
            return { success: false, code };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(false);
        }
    };

    const refreshToken = async () => {
        try {
            await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
            await checkAuth();
        } catch (error) {
            setUser(false);
        }
    };

    const value = {
        user,
        loading,
        login,
        register,
        resendVerification,
        verifyEmail,
        logout,
        refreshToken,
        checkAuth,
        isAuthenticated: !!user && user !== false,
        isAdmin: user?.role === 'admin',
        isRecruiter: ['admin', 'recruiter', 'manager'].includes(user?.role),
        isBilling: ['admin', 'manager', 'billing_admin'].includes(user?.role),
        isBillingOnly: user?.role === 'billing_admin',
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
